/**
 * resourceRegistry.js — Phase 1 of the Project Resource architecture
 * (see docs/PROJECT-RESOURCE-ARCHITECTURE-PLAN.md).
 *
 * A pure lookup table of per-type resource adapters. It does not own any
 * data itself and does not change how any existing store persists or
 * behaves — each adapter just delegates to the store that already owns
 * that resource type (e.g. useDatasetStore). ResourcePicker.vue is the
 * primary consumer today (getAdapter().list()/.getDisplayName()); export,
 * import, and validation are future phases that would register against the
 * same table instead of hardcoding per-type logic.
 *
 * Every registered adapter is expected to implement the same shape:
 *   type: string
 *   list(): resource[]
 *   get(uuid): resource | null
 *   remove(uuid): void | Promise<void>
 *   duplicate(uuid): uuid | null | Promise<uuid|void>
 *   rename(uuid, name): void | Promise<void>
 *   isUsed(uuid, layer): boolean
 *   getDisplayName(uuid): string | null
 * Methods may be synchronous or return a Promise depending on the backing
 * store (in-memory Pinia state vs. IndexedDB) — callers should not assume
 * either. See datasetResourceAdapter.js/fontResourceAdapter.js (sync) and
 * imageResourceAdapter.js (async) for both shapes in practice.
 */

const adapters = new Map()

/** Register (or replace) the adapter for a resource type, e.g. 'dataset'. */
export function registerAdapter(type, adapter) {
  adapters.set(type, adapter)
}

/** Remove a previously registered adapter. Mainly useful for tests. */
export function unregisterAdapter(type) {
  adapters.delete(type)
}

/** The adapter registered for `type`, or null if none is registered. */
export function getAdapter(type) {
  return adapters.get(type) ?? null
}

/** All currently registered adapters. */
export function getAdapters() {
  return Array.from(adapters.values())
}

/**
 * Adapters that declare a `packageFolder` — the only ones a Design Package
 * (see docs plan "Design Packages") ever touches. Keeps the package layer
 * fully generic: it iterates this list, never a hardcoded resource type.
 */
export function getPackageableAdapters() {
  return getAdapters().filter(a => a.packageFolder)
}

/** Look up a single resource by type + uuid via its registered adapter. */
export function getResource(type, uuid) {
  return getAdapter(type)?.get(uuid) ?? null
}

/** List all resources of a given type via its registered adapter. */
export function listResources(type) {
  return getAdapter(type)?.list() ?? []
}
